import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Search as SearchIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useSnackbar } from 'notistack';
import loteInformesService from '../../services/loteInformesService';
import type { LoteMovimientoDto } from '../../types/lotes-informes';
import { getApiErrorMessage } from '../../utils/apiError';

const LotesMovimientosInforme: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<LoteMovimientoDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [codigoArticulo, setCodigoArticulo] = useState('');
  const [nroLote, setNroLote] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const load = async () => {
    try {
      const data = await loteInformesService.movimientos({
        codigoArticulo: codigoArticulo || undefined,
        nroLote: nroLote || undefined,
        desde: desde || undefined,
        hasta: hasta || undefined,
        page: page + 1,
        pageSize,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al consultar'), { variant: 'error' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      items.map((i) => ({
        Fecha: i.fecha,
        Articulo: i.codigoArticulo,
        Descripcion: i.descripcionArticulo,
        Lote: i.nroLote,
        Tipo: i.tipoMov,
        Deposito: i.nroUbicacion,
        Cantidad: i.cantidad,
        Formulario: i.nroIDFormulario,
        Usuario: i.usuario,
      })),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');
    XLSX.writeFile(wb, 'lotes-movimientos.xlsx');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
            Movimientos de lotes
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField size="small" label="Artículo" value={codigoArticulo} onChange={(e) => setCodigoArticulo(e.target.value)} />
            <TextField size="small" label="Lote" value={nroLote} onChange={(e) => setNroLote(e.target.value)} />
            <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={desde} onChange={(e) => setDesde(e.target.value)} />
            <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={hasta} onChange={(e) => setHasta(e.target.value)} />
            <Button variant="contained" startIcon={<SearchIcon />} onClick={() => { setPage(0); load(); }}>Buscar</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportExcel}>Excel</Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Artículo</TableCell>
                  <TableCell>Lote</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Depósito</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((i) => (
                  <TableRow key={i.idMovimiento}>
                    <TableCell>{new Date(i.fecha).toLocaleString()}</TableCell>
                    <TableCell>{i.codigoArticulo}</TableCell>
                    <TableCell>{i.nroLote}</TableCell>
                    <TableCell>{i.tipoMov}</TableCell>
                    <TableCell>{i.nroUbicacion}</TableCell>
                    <TableCell align="right">{i.cantidad}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default LotesMovimientosInforme;
